import { useEffect, useState } from 'react';
import { api, type Deal } from '../api';
import { relativeTime } from './DealTag';
import { VisionSection } from './VisionSection';
import { SaleContents } from './SaleContents';
import { OfferTermsPanel } from './OfferTerms';

/**
 * Deal detail + the voting control.
 *
 * The votes answer one question — "is this still worth the trip?" — so the
 * buttons are labelled with that question's answers rather than with abstract
 * up/down arrows, and the down-vote asks *why* so the deal can be retired
 * automatically when enough people report it gone.
 */
export function DealPanel({
  dealId,
  onClose,
  onVoted,
}: {
  dealId: string;
  onClose: () => void;
  onVoted: (deal: Deal) => void;
}) {
  const [deal, setDeal] = useState<Deal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .deal(dealId)
      .then((r) => !cancelled && setDeal(r.deal))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  // Escape closes the panel — it's a modal surface, so it needs a keyboard exit.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function vote(value: 1 | -1, reason?: string) {
    if (!deal || busy) return;
    setBusy(true);
    try {
      // Clicking the active choice again clears the vote.
      const next = deal.votes.mine === value ? 0 : value;
      const result = await api.vote(deal.id, next as 1 | -1 | 0, next === 0 ? undefined : reason);
      const updated: Deal = {
        ...deal,
        status: result.status,
        votes: { ...deal.votes, ...result.votes },
      };
      setDeal(updated);
      onVoted(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Vote failed');
    } finally {
      setBusy(false);
    }
  }

  // Hero first, then extras, without repeating it — `imageUrl` is normally also
  // the first entry in `images`.
  const photos = deal
    ? [...new Set([deal.imageUrl, ...(deal.images ?? [])].filter((u): u is string => !!u))]
    : [];

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="panel" role="dialog" aria-modal="true" aria-label="Deal details">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="tag-cat" style={{ color: 'var(--field-text-dim)' }}>
            {deal ? `${deal.categoryEmoji} ${deal.categoryLabel}` : 'Loading'}
          </span>
          <button className="panel-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {error && <p className="error">{error}</p>}
        {!deal && !error && <p style={{ color: 'var(--field-text-dim)' }}>Loading deal…</p>}

        {deal && (
          <>
            {photos.length > 0 && (
              <div className={photos.length > 1 ? 'photo-strip' : 'photo-single'}>
                {photos.map((src) => (
                  <img key={src} src={src} alt="" loading="lazy" />
                ))}
              </div>
            )}

            <div className="panel-price" style={{ marginTop: 18 }}>
              {/* A range beats the single number: quoting only the low end of
                  $189.99–$679.99 promises a price most buyers will not get. */}
              {deal.price.rangeDisplay ?? deal.price.display ?? 'Sale'}
              {deal.price.wasDisplay && <span className="tag-was">{deal.price.wasDisplay}</span>}
            </div>
            {deal.price.rangeDisplay && (
              <p style={{ color: 'var(--field-text-dim)', fontSize: 13, margin: '6px 0 0' }}>
                Price varies by option
              </p>
            )}
            {(deal.mentionCount ?? 1) > 1 && (
              <p style={{ color: 'var(--positive, #4ECB94)', fontSize: 12, margin: '6px 0 0' }}>
                Posted by {deal.mentionCount} sources
              </p>
            )}
            {deal.price.note && (
              <p style={{ color: 'var(--field-text-dim)', fontSize: 13, margin: '6px 0 0' }}>
                {deal.price.note}
              </p>
            )}

            <h2>{deal.itemName}</h2>
            <p style={{ color: 'var(--field-text-dim)', fontSize: 13, lineHeight: 1.5 }}>
              {deal.title}
            </p>

            <div className="vote-bar">
              <button
                className="vote up"
                aria-pressed={deal.votes.mine === 1}
                onClick={() => vote(1, 'still_good')}
                disabled={busy}
              >
                Still good
                <small>{deal.votes.up} confirmed</small>
              </button>
              <button
                className="vote down"
                aria-pressed={deal.votes.mine === -1}
                onClick={() => vote(-1, 'expired')}
                disabled={busy}
              >
                Gone
                <small>{deal.votes.down} reported</small>
              </button>
            </div>

            {deal.status !== 'active' && (
              <p className="error">
                This deal is marked {deal.status}. Enough people reported it gone.
              </p>
            )}

            {/* Sits above the sale listings deliberately: for a free pickup
                this is the only thing on the panel that answers "is this worth
                the walk", and it must not be below the fold. */}
            <VisionSection deal={deal} onUpdate={setDeal} />

            <SaleContents deal={deal} />

            {/* A deal with no product list still has conditions, and "is there
                a limit" is the same question either way. */}
            {!deal.listings?.length && (deal.price.terms ?? deal.price.offer) && (
              <OfferTermsPanel offer={(deal.price.terms ?? deal.price.offer)!} />
            )}

            <dl style={{ margin: '18px 0 0' }}>
              <Row label="Where" value={deal.merchant ?? 'Not stated'} />
              <Row
                label="Availability"
                value={[
                  deal.availability.online && 'Online',
                  deal.availability.inStore && 'In store',
                ]
                  .filter(Boolean)
                  .join(' · ') || 'Unknown'}
              />
              {deal.distanceDisplay && <Row label="Distance" value={`${deal.distanceDisplay} away`} />}
              {deal.couponCode && <Row label="Code" value={deal.couponCode} />}
              {deal.brand && <Row label="Brand" value={deal.brand} />}
              <Row label="Posted" value={relativeTime(deal.postedAt)} />
              {deal.expiresAt && <Row label="Ends" value={new Date(deal.expiresAt).toLocaleString()} />}
            </dl>

            {deal.locations.length > 0 && (
              <>
                <p className="form-label">Pickup locations</p>
                {deal.locations.map((l) => (
                  <p key={l.id} style={{ fontSize: 13, color: 'var(--field-text)', margin: '0 0 8px' }}>
                    {l.label ?? l.city ?? 'Store'}
                    {l.address && (
                      <span style={{ display: 'block', color: 'var(--field-text-dim)', fontSize: 12 }}>
                        {l.address}
                      </span>
                    )}
                  </p>
                ))}
              </>
            )}

            <div className="btn-row">
              {deal.url && (
                <a className="btn" href={deal.url} target="_blank" rel="noreferrer noopener">
                  Open deal
                </a>
              )}
              {deal.tags.length > 0 && (
                <span
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 11,
                    color: 'var(--field-text-dim)',
                    alignSelf: 'center',
                  }}
                >
                  {deal.tags.join(' · ')}
                </span>
              )}
            </div>
          </>
        )}
      </aside>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="field-row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
